import React, { useState, useEffect, useRef } from 'react';
import api from '../apiConfig/api';
import * as XLSX from 'xlsx';

const ReportDownloadPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState('');
  const [initialWards, setInitialWards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedWards, setSelectedWards] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [filteredWards, setFilteredWards] = useState([]);
  // Add these new state variables
  const [availableZonesForOrg, setAvailableZonesForOrg] = useState([]);
  const [selectedZoneForOrg, setSelectedZoneForOrg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showWardDropdown, setShowWardDropdown] = useState(false);
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);
  const [filter, setFilter] = useState('all');
  const dropdownRef = useRef(null);
  const zoneDropdownRef = useRef(null);
  const [selectedOrgCode, setSelectedOrgCode] = useState('');

  // Zone filter states
  const [availableZones, setAvailableZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);

  const isCMAUser = localStorage.getItem('org_code') === "CMA";

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const orgCode = localStorage.getItem('org_code');
        const token = localStorage.getItem('token');
        
        if (orgCode === "CMA") {
          const response = await api.get('https://ras-tndma.in/api/prod/user/all-org');
          if (response.data && response.data.data) {
            const orgs = response.data.data.map(org => ({
              org_code: org.org_code,
              org_name: org.org_name,
              total: org.total,
              completed: org.completed,
              pending: org.pending
            }));
            setOrganizations(orgs);
          }
        } else {
          // For non-CMA users
          const orgName = localStorage.getItem('organization_name');
          const localOrgCode = localStorage.getItem('organization_code');
          
          if (orgName) {
            setOrganization(orgName);
            setSelectedOrg(orgName);
            setSelectedOrgCode(localOrgCode);
            
            // Fetch zones and wards for this organization immediately
            const response = await api.get(`https://ras-tndma.in/api/prod/user/asst_smry`, {
              params: {
                org: localOrgCode
              },
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            console.log('API Response for non-CMA user:', response.data);

            if (response.data?.data) {
              // Extract unique zones
              const zones = [...new Set(response.data.data
                .map(item => item.zone_name))]
                .filter(Boolean);
              
              console.log('Available zones:', zones);
              setAvailableZonesForOrg(zones);
              
              // Store all ward data with zone info
              const allWards = response.data.data.map(ward => ({
                WardID: ward.ward_id || ward.id || String(Math.random()),
                WarDName: ward.ward_name,
                ZoneName: ward.zone_name
              }));
              
              setInitialWards(allWards);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('Failed to load organization data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowWardDropdown(false);
      }
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target)) {
        setShowZoneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract zones from report data and apply zone filtering
  useEffect(() => {
    if (reportData.length > 0) {
      // Apply zone filtering based on the new selectedZoneForOrg
      let filtered = reportData;
      if (selectedZoneForOrg && selectedZoneForOrg !== 'all') {
        filtered = reportData.filter(item => item.zone_name === selectedZoneForOrg);
      }
      
      // Apply filter-specific restrictions
      if (filter === 'Completed') {
        filtered = filtered.filter(item => item.completed > 0);
      } else if (filter === 'Pending') {
        filtered = filtered.filter(item => item.pending > 0);
      } else if (filter === 'Variations') {
        filtered = filtered.filter(item => 
          (item.usage_vary > 0) || 
          (item.area_vary > 0) || 
          (item.both_vary > 0) ||
          (item.variations > 0)
        );
      }
      
      setFilteredReportData(filtered);
    } else {
      setFilteredReportData([]);
    }
  }, [reportData, selectedZoneForOrg, filter]);

  const handleZoneForOrgChange = (zoneName) => {
    setSelectedZoneForOrg(zoneName);
    if (zoneName !== 'all') {
      setSelectedWards([]); // Clear selected wards when zone changes
    }
  };

  const fetchWardsByOrg = async (orgCode) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await api.get(`/asset/org/${orgCode}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
        
      if (response.data?.data?.[0]?.wards) {
        const transformedWards = response.data.data[0].wards.map((wardName, index) => ({
          WardID: index.toString(),
          WarDName: wardName
        }));
        setInitialWards(transformedWards);
        setSelectedWards([]);
      }
    } catch (error) {
      console.error('Error fetching wards:', error);
      setError('Failed to load wards');
      setInitialWards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = async (e) => {
    const selectedIndex = e.target.selectedIndex;
    const selectedOption = e.target.options[selectedIndex];
    const selectedOrgName = selectedOption.text;
    const selectedOrgCode = e.target.value;
    
    setSelectedOrg(selectedOrgName);
    setSelectedOrgCode(selectedOrgCode);
    setSelectedWards([]);
    setSelectedZones([]); // Reset zone selection when org changes
    setSelectedZoneForOrg(''); // Reset zone selection
    
    if (selectedOrgCode && selectedOrgCode !== 'all') {
      await fetchWardsByOrgSummary(selectedOrgCode);
    } else if (selectedOrgCode === 'all') {
      // For "All" organizations, clear zones and wards
      setAvailableZonesForOrg([]);
      setInitialWards([]);
    }
  };

  const fetchWardsByOrgSummary = async (orgCode) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await api.get(`https://ras-tndma.in/api/prod/user/asst_smry`, {
        params: {
          org: orgCode
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data?.data) {
        // Extract unique zones
        const zones = [...new Set(response.data.data.map(item => item.zone_name))].filter(Boolean);
        setAvailableZonesForOrg(zones);
        setSelectedZoneForOrg(''); // Reset selected zone
        
        // Store all ward data but don't filter yet
        const allWards = response.data.data.map(ward => ({
          WardID: ward.ward_id || ward.id || String(Math.random()),
          WarDName: ward.ward_name,
          ZoneName: ward.zone_name
        }));
        
        // Keep all ward data for filtering by zone later
        setInitialWards(allWards);
        setSelectedWards([]);
      } else {
        console.log('No ward data in API response');
        setAvailableZonesForOrg([]);
        setInitialWards([]);
      }
    } catch (error) {
      console.error('Error fetching wards:', error);
      setError('Failed to load wards');
      setAvailableZonesForOrg([]);
      setInitialWards([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async () => {
    // For CMA users with "All" selections, allow API call without parameters
    if (isCMAUser && (selectedOrgCode === 'all' || selectedZoneForOrg === 'all' || selectedWards.includes('all'))) {
      // Call API without parameters for "All" selections
      await fetchReportDataWithoutParams();
      return;
    }

    // Original logic for specific selections
    if (!selectedOrgCode || selectedWards.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const orgCode = localStorage.getItem('org_code');
      const token = localStorage.getItem('token');
    
      if (orgCode === "CMA") {
        const wardsParam = selectedWards.join(', ');
        const endpoint = `/survey/report/${filter.toLowerCase()}`;
        const response = await api.get(endpoint, {
          params: {
            org: selectedOrgCode,
            ward: wardsParam
          }
        });
    
        if (response.data && !response.data.error) {
          setReportData(response.data.data || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch data');
        }
      } else {
        const formattedWards = selectedWards.join(',');
        const response = await api.get(`https://ras-tndma.in/api/prod/survey/report/${filter.toLowerCase()}`, {
          params: {
            org: selectedOrgCode,
            ward: formattedWards
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
    
        if (response.data && !response.data.error) {
          setReportData(response.data.data || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch data');
        }
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch data without parameters
  const fetchReportDataWithoutParams = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = `/survey/report/${filter.toLowerCase()}`;
      const response = await api.get(endpoint);
  
      if (response.data && !response.data.error) {
        setReportData(response.data.data || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // For CMA users with "All" selections
    if (isCMAUser && (selectedOrgCode === 'all' || selectedZoneForOrg === 'all' || selectedWards.includes('all'))) {
      fetchReportData();
      return;
    }

    // Original logic
    if (selectedOrgCode && selectedWards.length > 0) {
      fetchReportData();
    }
  }, [selectedOrgCode, selectedWards, filter, selectedZoneForOrg]);

  const handleWardToggle = (wardName) => {
    if (wardName === 'all') {
      if (selectedWards.includes('all')) {
        setSelectedWards([]);
      } else {
        setSelectedWards(['all']);
      }
      return;
    }

    console.log('Toggling ward:', wardName);
    setSelectedWards(prev => {
      // If "all" is currently selected, remove it when selecting specific wards
      const filteredPrev = prev.filter(w => w !== 'all');
      
      if (filteredPrev.includes(wardName)) {
        return filteredPrev.filter(w => w !== wardName);
      } else {
        return [...filteredPrev, wardName];
      }
    });
  };
  
  const handleSelectAllWards = () => {
    if (isCMAUser && selectedOrgCode === 'all') {
      // For "All" organizations, just toggle the "all" option
      if (selectedWards.includes('all')) {
        setSelectedWards([]);
      } else {
        setSelectedWards(['all']);
      }
      return;
    }

    const filteredWards = initialWards
      .filter(ward => !selectedZoneForOrg || selectedZoneForOrg === 'all' || ward.ZoneName === selectedZoneForOrg);
    
    const filteredWardNames = filteredWards.map(ward => ward.WarDName);
    
    if (selectedWards.length === filteredWardNames.length) {
      setSelectedWards([]);
    } else {
      setSelectedWards(filteredWardNames);
    }
  };

  const handleZoneToggle = (zoneName) => {
    setSelectedZones(prev => {
      if (prev.includes(zoneName)) {
        return prev.filter(z => z !== zoneName);
      } else {
        return [...prev, zoneName];
      }
    });
  };

  const handleSelectAllZones = () => {
    if (selectedZones.length === availableZones.length) {
      setSelectedZones([]);
    } else {
      setSelectedZones([...availableZones]);
    }
  };

  // Modified handleExportToExcel function
  const handleExportToExcel = async () => {
    // For CMA users with "All" selections, allow export without specific parameters
    if (isCMAUser && (selectedOrgCode === 'all' || selectedZoneForOrg === 'all' || selectedWards.includes('all'))) {
      await exportWithoutParams();
      return;
    }

    // Original logic for specific selections
    if (!selectedOrgCode || selectedWards.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const orgCode = localStorage.getItem('org_code');
      const token = localStorage.getItem('token');
      
      let response;
      
      // API call based on user type - specify responseType as 'json'
      if (orgCode === "CMA") {
        const endpoint = `/survey/report/data/${filter.toLowerCase()}`;
        response = await api.get(endpoint, {
          params: {
            org: selectedOrgCode,
            ward: selectedWards.join(',')
          },
          responseType: 'json'
        });
      } else {
        const endpoint = `https://ras-tndma.in/api/prod/survey/report/data/${filter.toLowerCase()}`;
        response = await api.get(endpoint, {
          params: {
            org: selectedOrgCode,
            ward: selectedWards.join(',')
          },
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'json'
        });
      }
      
      await processExcelExport(response);
    } catch (error) {
      console.error('Excel Export Error:', error);
      console.error('Stack trace:', error.stack);
      setError(error.message || 'Failed to download report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // New function to export without parameters
  const exportWithoutParams = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = `/survey/report/data/${filter.toLowerCase()}`;
      const response = await api.get(endpoint, {
        responseType: 'json'
      });
      
      await processExcelExport(response);
    } catch (error) {
      console.error('Excel Export Error:', error);
      setError(error.message || 'Failed to download report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process excel export
  const processExcelExport = async (response) => {
    // Log the response for debugging
    console.log('Excel Export Response:', response);
    console.log('Response Data Type:', typeof response.data);
    console.log('Response Data:', response.data);
    
    // Handle different response types
    let parsedData;
    
    // If response.data is a Blob, we need to read it
    if (response.data instanceof Blob) {
      console.log('Response is a Blob, parsing...');
      const text = await response.data.text();
      parsedData = JSON.parse(text);
    } 
    // If it's already parsed JSON
    else if (typeof response.data === 'object') {
      parsedData = response.data;
    } 
    // If it's a string, parse it
    else if (typeof response.data === 'string') {
      parsedData = JSON.parse(response.data);
    } 
    else {
      throw new Error('Unexpected response type');
    }
    
    console.log('Parsed Data:', parsedData);
    
    // Extract the data array from the parsed response
    let data;
    if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
      data = parsedData.data;
    } else {
      console.error('Invalid response structure:', parsedData);
      throw new Error('Invalid response format - no data array found');
    }

    // Apply zone filtering to export data
    if (selectedZoneForOrg && selectedZoneForOrg !== 'all') {
      data = data.filter(item => item.zone_name === selectedZoneForOrg);
    }
    
    // Validate data
    if (!data || data.length === 0) {
      console.warn('No data available for export');
      throw new Error('No data available for export');
    }
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    const formattedDate = `${day}.${month}.${year}`;
    
    const reportTitle = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE(${formattedDate})`;
    
    // Create headers based on the data keys, maintaining the order from the API
    const sampleRow = data[0];
    const dataKeys = Object.keys(sampleRow);
    
    // Map the keys to user-friendly headers
    const headerMap = {
      's_no': 'S.No',
      'zone_name': 'Zone Name',
      'ward_name': 'Ward Name',
      'street_name': 'Street Name',
      'assessment#': 'Assessment Number',
      'owner': 'Owner',
      'given_area': 'Given Area',
      'observed_area': 'Observed Area',
      'given_usage': 'Given Usage',
      'observed_usage': 'Observed Usage',
      'surveyor': 'Surveyor',
      'submitted': 'Submitted',
      'status': 'Status'
    };
    
    const headers = dataKeys.map(key => headerMap[key] || key.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '));
    
    // Convert data to array of arrays for Excel
    const excelData = [];
    
    // Add title
    excelData.push([reportTitle]);
    
    // Add empty row after title
    excelData.push([]);
    
    // Add headers
    excelData.push(headers);
    
    // Add data rows
    data.forEach((row) => {
      const rowData = dataKeys.map(key => {
        const value = row[key];
        // Handle null/undefined values
        return value !== null && value !== undefined ? value : '';
      });
      excelData.push(rowData);
    });
    
    // Create worksheet from array of arrays
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Merge cells for title (accounting for the extra empty row)
    if (headers.length > 1) {
      ws['!merges'] = [{
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 }
      }];
    }
    
    // Set column widths based on content
    const columnWidths = headers.map((header, idx) => {
      let maxLength = header.length;
      
      // Check data rows for max length
      data.forEach(row => {
        const cellContent = String(row[dataKeys[idx]] || '');
        maxLength = Math.max(maxLength, cellContent.length);
      });
      
      // Set reasonable width limits
      return { wch: Math.min(50, Math.max(10, maxLength + 2)) };
    });
    
    ws['!cols'] = columnWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Survey Data');
    
    // Write the Excel file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    // Download the file
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const downloadDate = new Date().toISOString().split('T')[0];
    const orgFilter = selectedOrgCode === 'all' ? 'all_orgs' : selectedOrgCode;
    const zoneFilter = selectedZoneForOrg === 'all' ? '_all_zones' : selectedZoneForOrg ? `_zone_${selectedZoneForOrg.replace(/\s+/g, '_')}` : '';
    const wardFilter = selectedWards.includes('all') ? '_all_wards' : '';
    const filename = `survey_report_${orgFilter}_${filter.toLowerCase()}${zoneFilter}${wardFilter}_${downloadDate}.xlsx`;
    
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    setError(null);
    console.log('Excel export successful');
  };

  // Updated renderTableHeaders to show all columns
  const renderTableHeaders = () => {
    const headerClasses = "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
    const rightAlignedHeader = "px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider";
  
    switch (filter) {
      case 'Completed':
        return (
          <tr>
            <th className={headerClasses}>Organization</th>
            <th className={headerClasses}>Zone</th>
            <th className={headerClasses}>Ward</th>
            <th className={rightAlignedHeader}>Total</th>
            <th className={rightAlignedHeader}>Completed</th>
            <th className={rightAlignedHeader}>Variations</th>
          </tr>
        );
      case 'Pending':
        return (
          <tr>
            <th className={headerClasses}>Organization</th>
            <th className={headerClasses}>Zone</th>
            <th className={headerClasses}>Ward</th>
            <th className={rightAlignedHeader}>Total</th>
            <th className={rightAlignedHeader}>Pending</th>
          </tr>
        );
      case 'Variations':
        return (
          <tr>
            <th className={headerClasses}>Organization</th>
            <th className={headerClasses}>Zone</th>
            <th className={headerClasses}>Ward</th>
            <th className={rightAlignedHeader}>Total</th>
            <th className={rightAlignedHeader}>Completed</th>
            <th className={rightAlignedHeader}>Variations by Usage</th>
            <th className={rightAlignedHeader}>Variations by Area</th>
            <th className={rightAlignedHeader}>Variations by Both</th>
          </tr>
        );
      default:
        return (
          <tr>
            <th className={headerClasses}>Organization</th>
            <th className={headerClasses}>Zone</th>
            <th className={headerClasses}>Ward</th>
            <th className={rightAlignedHeader}>Completed</th>
            <th className={rightAlignedHeader}>Pending</th>
            <th className={rightAlignedHeader}>Total</th>
          </tr>
        );
    }
  };
  
  const renderTableRow = (report, index) => {
    const cellClasses = "px-4 py-3 whitespace-nowrap text-sm text-gray-900";
    const numberCellClasses = "px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right";
    const completedCellClasses = "px-4 py-3 whitespace-nowrap text-sm text-green-600 font-medium text-right";
    const pendingCellClasses = "px-4 py-3 whitespace-nowrap text-sm text-amber-600 font-medium text-right";
  
    switch (filter) {
      case 'Completed':
        return (
          <tr key={`${report.ward_name}-${index}`} className="hover:bg-gray-50">
            <td className={cellClasses}>{report.org_name}</td>
            <td className={cellClasses}>{report.zone_name}</td>
            <td className={cellClasses}>{report.ward_name}</td>
            <td className={numberCellClasses}>{report.total}</td>
            <td className={completedCellClasses}>{report.completed}</td>
            <td className={numberCellClasses}>
              {report.variations || report.usage_vary || '0'}
            </td>
          </tr>
        );
      case 'Variations':
        return (
          <tr key={`${report.ward_name}-${index}`} className="hover:bg-gray-50">
            <td className={cellClasses}>{report.org_name}</td>
            <td className={cellClasses}>{report.zone_name}</td>
            <td className={cellClasses}>{report.ward_name}</td>
            <td className={numberCellClasses}>{report.total}</td>
            <td className={numberCellClasses}>{report.completed}</td>
            <td className={numberCellClasses}>{report.usage_vary || '0'}</td>
            <td className={numberCellClasses}>{report.area_vary || '0'}</td>
            <td className={numberCellClasses}>{report.both_vary || '0'}</td>
          </tr>
        );
      case 'Pending':
        return (
          <tr key={`${report.ward_name}-${index}`} className="hover:bg-gray-50">
            <td className={cellClasses}>{report.org_name}</td>
            <td className={cellClasses}>{report.zone_name}</td>
            <td className={cellClasses}>{report.ward_name}</td>
            <td className={numberCellClasses}>{report.total}</td>
            <td className={pendingCellClasses}>{report.pending}</td>
          </tr>
        );
      default:
        return (
          <tr key={`${report.ward_name}-${index}`} className="hover:bg-gray-50">
            <td className={cellClasses}>{report.org_name}</td>
            <td className={cellClasses}>{report.zone_name}</td>
            <td className={cellClasses}>{report.ward_name}</td>
            <td className={completedCellClasses}>{report.completed}</td>
            <td className={pendingCellClasses}>{report.pending}</td>
            <td className={numberCellClasses}>{report.total}</td>
          </tr>
        );
    }
  };

  // Helper function to check if export should be enabled
  const isExportEnabled = () => {
    if (isCMAUser) {
      // For CMA users, allow export if any selection is made or if "All" is selected
      return selectedOrgCode === 'all' || 
             selectedZoneForOrg === 'all' || 
             selectedWards.includes('all') || 
             (selectedOrgCode && selectedWards.length > 0);
    }
    // For non-CMA users, use original logic
    return selectedOrgCode && selectedWards.length > 0;
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-lg rounded-lg overflow-hidden isolation-auto">
        <div className="px-6 py-4 bg-[#75d1e3] text-black">
          <div className="flex flex-wrap gap-4 items-end justify-between relative">
            <div className="flex flex-wrap gap-4">
              <div className="relative">
                <label className="block text-sm font-medium mb-1">Organization</label>
                <select
                  value={selectedOrgCode}
                  onChange={handleOrgChange}
                  className="px-3 py-2 bg-white rounded-md w-64 text-gray-700 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select Organization</option>
                  {isCMAUser ? (
                    <>
                      <option value="all">All Organizations</option>
                      {organizations.map((org, index) => (
                        <option key={index} value={org.org_code}>
                          {org.org_name}
                        </option>
                      ))}
                    </>
                  ) : (
                    <option value={localStorage.getItem('organization_code')}>{organization}</option>
                  )}
                </select>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium mb-1">Zone</label>
                <select
                  value={selectedZoneForOrg}
                  onChange={(e) => handleZoneForOrgChange(e.target.value)}
                  className="px-3 py-2 bg-white rounded-md w-64 text-gray-700 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  disabled={!selectedOrgCode || (selectedOrgCode !== 'all' && availableZonesForOrg.length === 0)}
                >
                  <option value="">Select Zone</option>
                  {isCMAUser && selectedOrgCode === 'all' && (
                    <option value="all">All Zones</option>
                  )}
                  {availableZonesForOrg.map((zone, index) => (
                    <option key={index} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium mb-1">Ward</label>
                <button
                  type="button"
                  onClick={() => setShowWardDropdown(!showWardDropdown)}
                  className="px-3 py-2 bg-white rounded-md w-64 text-gray-700 text-left flex justify-between items-center border border-gray-300 hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <span className="truncate">
                    {selectedWards.includes('all') 
                      ? 'All Wards'
                      : selectedWards.length 
                        ? `${selectedWards.length} ward${selectedWards.length > 1 ? 's' : ''} selected`
                        : 'Select Wards'}
                  </span>
                  <svg 
                    className={`w-4 h-4 transition-transform duration-200 ${showWardDropdown ? 'transform rotate-180' : ''}`} 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {showWardDropdown && (
                  <div 
                    className="fixed inset-0"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      pointerEvents: 'none',
                      zIndex: 40
                    }}
                  >
                    <div
                      className="absolute bg-white rounded-md shadow-xl border border-gray-200"
                      style={{
                        top: dropdownRef.current?.getBoundingClientRect().bottom + window.scrollY + 4,
                        left: dropdownRef.current?.getBoundingClientRect().left,
                        width: dropdownRef.current?.offsetWidth,
                        pointerEvents: 'auto'
                      }}
                    >
                      <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                        <label className="flex items-center px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedWards.includes('all') || (selectedWards.length === initialWards.length && initialWards.length > 0)}
                            onChange={handleSelectAllWards}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm">
                            {isCMAUser && selectedOrgCode === 'all' ? 'All Wards' : 'Select All Wards'}
                          </span>
                        </label>
                      </div>

                      <div className="max-h-60 overflow-y-auto">
                        {isCMAUser && selectedOrgCode === 'all' && (
                          <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedWards.includes('all')}
                              onChange={() => handleWardToggle('all')}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm font-medium">All Wards</span>
                          </label>
                        )}
                        
                        {initialWards
                          .filter(ward => !selectedZoneForOrg || selectedZoneForOrg === 'all' || ward.ZoneName === selectedZoneForOrg)
                          .map((ward) => (
                            <label 
                              key={ward.WardID} 
                              className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedWards.includes(ward.WarDName)}
                                onChange={() => handleWardToggle(ward.WarDName)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                disabled={selectedWards.includes('all')}
                              />
                              <span className="ml-2 text-sm">{ward.WarDName}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Filter</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-3 py-2 bg-white rounded-md w-64 text-gray-700 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Variations">Variations</option>
                </select>
              </div>
            </div>

            <button 
              onClick={handleExportToExcel}
              disabled={loading || !isExportEnabled()}
              className="flex items-center px-4 py-2 bg-white text-black rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12V3c0-.55-.45-1-1-1s-1 .45-1 1v9l-3.5-3.5c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41l5.2 5.2c.39.39 1.02.39 1.41 0l5.2-5.2c.39-.39.39-1.02 0-1.41-.39-.39-1.02-.39-1.41 0L10 12z"/>
              </svg>
              {loading ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
        </div>

        <div className="px-6 py-4 relative z-10">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
                {error}
              </div>
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!loading && filteredReportData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {renderTableHeaders()}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReportData.map((report, index) => renderTableRow(report, index))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filteredReportData.length === 0 && reportData.length > 0 && (
            <div className="text-center py-8">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data for selected zones</h3>
              <p className="mt-1 text-sm text-gray-500">
                No records found for the selected zone filter. Try selecting different zones or clear the zone filter.
              </p>
            </div>
          )}

          {!loading && reportData.length === 0 && (
            <div className="text-center py-8">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
              <p className="mt-1 text-sm text-gray-500">
                {selectedOrg && (selectedWards.length > 0 || selectedWards.includes('all'))
                  ? `No ${filter.toLowerCase() !== 'all' ? filter.toLowerCase() : ''} records found for the selected criteria.`
                  : 'Select an organization and at least one ward to view the report data.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportDownloadPage;